import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { canAccessPath } from '../utils/featureAccess';
import {
  INTELLIGENCE_DROPDOWN,
  INTELLIGENCE_NAV_ITEMS,
  PLATFORM_NAV_ITEMS,
  PREDICTION_DROPDOWN,
  PREDICTION_NAV_ITEMS,
} from '../config/platformNav';

function NavDropdown({ label, icon: Icon, items, isActive, activePath }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [activePath]);

  const linkClass = (itemActive) =>
    `flex items-center space-x-3 px-4 py-3 text-sm font-medium ${
      itemActive
        ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
        : 'text-secondary-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;

  const triggerClass = `inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
    isActive
      ? 'bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300 border border-[#03396c]/30 dark:border-blue-700'
      : 'text-gray-600 dark:text-gray-400 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
  }`;

  return (
    <div className="relative shrink-0" ref={triggerRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!open) updatePosition();
          setOpen((prev) => !prev);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className={triggerClass}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Icon className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
        <span>{label}</span>
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[200] min-w-[16rem] bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1"
            style={{ top: menuPos.top, left: menuPos.left }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {items.map((item) => {
              const ItemIcon = item.icon;
              const itemActive = activePath === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setOpen(false)}
                  className={linkClass(itemActive)}
                >
                  <ItemIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="leading-snug">{item.label}</span>
                </Link>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Platform links for Policy Maker workspace — inline with logo and account menu. */
const PolicyMakerPlatformNav = () => {
  const location = useLocation();
  const { user } = useAuth();
  const features = user?.subscription_plan_features || [];

  const visiblePlatformItems = PLATFORM_NAV_ITEMS.filter(
    (item) => item.path !== '/' && canAccessPath(item.path, features, user),
  );
  const visibleIntelligenceItems = INTELLIGENCE_NAV_ITEMS.filter((item) =>
    canAccessPath(item.path, features, user),
  );
  const visiblePredictionItems = PREDICTION_NAV_ITEMS.filter((item) =>
    canAccessPath(item.path, features, user),
  );
  const isIntelligenceActive = INTELLIGENCE_NAV_ITEMS.some(
    (item) => item.path === location.pathname && canAccessPath(item.path, features, user),
  );
  const isPredictionActive = PREDICTION_NAV_ITEMS.some(
    (item) => item.path === location.pathname && canAccessPath(item.path, features, user),
  );

  const linkClass = (isActive) =>
    `inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
      isActive
        ? 'bg-[#03396c]/10 dark:bg-blue-900/40 text-[#03396c] dark:text-blue-300 border border-[#03396c]/30 dark:border-blue-700'
        : 'text-gray-600 dark:text-gray-400 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
    }`;

  return (
    <div className="flex items-center gap-1 py-1 min-w-max">
      {visiblePlatformItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link key={item.path} to={item.path} className={linkClass(isActive)}>
            <Icon className="h-3.5 w-3.5 shrink-0 hidden sm:block" />
            <span>{item.label}</span>
          </Link>
        );
      })}

      {visibleIntelligenceItems.length > 0 && (
        <NavDropdown
          label={INTELLIGENCE_DROPDOWN.label}
          icon={INTELLIGENCE_DROPDOWN.icon}
          items={visibleIntelligenceItems}
          isActive={isIntelligenceActive}
          activePath={location.pathname}
        />
      )}

      {visiblePredictionItems.length > 0 && (
        <NavDropdown
          label={PREDICTION_DROPDOWN.label}
          icon={PREDICTION_DROPDOWN.icon}
          items={visiblePredictionItems}
          isActive={isPredictionActive}
          activePath={location.pathname}
        />
      )}

    </div>
  );
};

export default PolicyMakerPlatformNav;
